import React, { createContext, ReactElement, useCallback, useContext, useState } from "react";
import { Button } from "@cofano/cck";
import { Dialog, DialogTitle, DialogActions, DialogContent } from "@mui/material";
import { Language } from "imports/Utils";

type ConfirmOptions = {
	type: "WARNING" | "DELETE",
	title?: string,
	content?: ReactElement,
	confirmText?: string,
	confirmDisabled?: boolean,
	hideCancel?: boolean,
	secondConfirmButton?: (func: () => void) => ReactElement
};

type ConfirmContextType = ((val: ConfirmOptions) => Promise<unknown>)

const ConfirmContext = createContext<ConfirmContextType>(() => Promise.resolve());

const ConfirmProvider = ({ children }: { children: ReactElement }) => {

	const [ options, setOptions ] = useState<ConfirmOptions>({ type: "DELETE" });
	const [ resolveReject, setResolveReject ] = useState<[(val?: []) => void, () => void] | []>([]);
	const [ resolve, reject ] = resolveReject;

	const confirm = useCallback((currentOptions = {} as ConfirmOptions) => {
		return new Promise((resolveFunction, rejectFunction) => {
			setOptions(currentOptions);
			setResolveReject([ resolveFunction, rejectFunction ]);
		});
	}, []);

	const handleClose = useCallback(() => {
		setResolveReject([]);
	}, []);

	const handleCancel = useCallback(() => {
		if (reject) {
			reject();
			handleClose();
		} else {
			handleClose();
		}
	}, [ reject, handleClose ]);

	const handleConfirm = useCallback(() => {
		if (resolve) {
			resolve();
			handleClose();
		}
	}, [ resolve, handleClose ]);

	const getTitle = useCallback((currentOptions: ConfirmOptions): string | ReactElement => {
		switch (currentOptions.type) {
			case "WARNING":
				return currentOptions.title || "";
			case "DELETE":
				return Language("CONFIRM_DELETE_DATA");
		}
	}, []);

	const getContent = useCallback((currentOptions: ConfirmOptions) => {
		return currentOptions.content;
	}, []);

	const getConfirmText = useCallback((currentOptions: ConfirmOptions) => {
		return currentOptions.confirmText;
	}, []);

	const getConfirmDisabled = useCallback((currentOptions: ConfirmOptions) => {
		return currentOptions.confirmDisabled;
	}, []);

	return (
		<React.Fragment>
			<ConfirmContext.Provider value={confirm}>
				{children}
			</ConfirmContext.Provider>

			<ConfirmationDialog
				open={resolveReject.length === 2}
				title={getTitle(options)}
				content={getContent(options)}
				confirmText={getConfirmText(options)}
				confirmDisabled={getConfirmDisabled(options)}
				hideCancel={options.hideCancel}
				onCancel={handleCancel}
				onConfirm={handleConfirm}
				secondConfirmButton={options.secondConfirmButton} />
		</React.Fragment>
	);
};


const ConfirmationDialog = ({ open, onCancel, onConfirm, title, content, confirmText, secondConfirmButton, confirmDisabled, hideCancel }: {
	open: boolean,
	onCancel: () => void,
	onConfirm: () => void,
	title: string | ReactElement,
	content?: ReactElement,
	confirmText?: string,
	confirmDisabled?: boolean,
	hideCancel?: boolean,
	secondConfirmButton?: (func: () => void) => ReactElement
}) => {
	return (
		<Dialog fullWidth open={open} onClose={onCancel}>
			{title && <DialogTitle style={{ color: "inherit" }}>{title}</DialogTitle>}

			{content && <DialogContent dividers>{content}</DialogContent>}

			<DialogActions>
				{!hideCancel && (
					<Button onClick={onCancel} variant="text" size="small">
						{Language("CANCEL")}
					</Button>
				)}

				{secondConfirmButton ? secondConfirmButton(onCancel) : <React.Fragment />}

				<Button onClick={onConfirm} disabled={confirmDisabled} size="small">
					{confirmText || Language("CONFIRM")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export const useConfirm = () => {
	return useContext(ConfirmContext);
};

export default ConfirmProvider;
